import { answerKey } from "@gierki/shared";
import { Karta, Komunikat, Odznaka, Przycisk } from "../ui.tsx";
import type { PmView } from "./PanstwaMiasta.tsx";

/**
 * Faza sprzeciwów.
 *
 * Wszystko jest domyślnie uznane — klikasz **wyłącznie** to, co kwestionujesz,
 * i naciskasz „Gotowe". Wersja, w której każdy głosuje nad każdą odpowiedzią,
 * oznaczałaby przy pięciu graczach i sześciu kategoriach piętnaście kliknięć
 * po każdej rundzie; tutaj typowo jest ich zero.
 *
 * Odpowiedzi ze słownika w ogóle się tu nie pojawiają jako klikalne — są już
 * zatwierdzone i nie ma o czym dyskutować.
 */
export function Sprzeciwy({
  pm,
  nicki,
  mojeId,
  onSprzeciw,
  onGotowe,
}: {
  pm: PmView;
  nicki: Record<string, string>;
  mojeId: string;
  onSprzeciw: (key: string, on: boolean) => void;
  onGotowe: () => void;
}) {
  const jestemGotowy = pm.ready.includes(mojeId);

  const opisStatusu: Record<string, string> = {
    blank: "puste",
    "wrong-letter": "zła litera",
    dictionary: "w słowniku",
  };

  const poKategorii = pm.categories.map((kategoria) => ({
    kategoria,
    wpisy: pm.evaluations.filter((wpis) => wpis.category === kategoria.key),
  }));

  return (
    <>
      <Komunikat>
        Wszystko jest zaliczone, dopóki ktoś nie zgłosi sprzeciwu. Kliknij tylko te odpowiedzi,
        które Twoim zdaniem się nie liczą.
      </Komunikat>

      {poKategorii.map(({ kategoria, wpisy }) => (
        <Karta key={kategoria.key}>
          <div className="text-sm text-tekst-slaby mb-2">{kategoria.label}</div>
          <div className="space-y-2">
            {wpisy.map((wpis) => {
              const key = answerKey(wpis.playerId, wpis.category);
              const mozna = pm.challengeable.includes(key) && wpis.playerId !== mojeId;
              const kwestionujacy = pm.objections[key] ?? [];
              const zakwestionowane = kwestionujacy.includes(mojeId);

              return (
                <button
                  key={key}
                  onClick={() => (mozna ? onSprzeciw(key, !zakwestionowane) : undefined)}
                  disabled={!mozna}
                  className={[
                    "w-full flex items-center justify-between gap-3 p-3 rounded-xl text-left transition-colors",
                    zakwestionowane
                      ? "bg-blad/20 border border-blad"
                      : "bg-tlo border border-obrys",
                    mozna ? "active:bg-panel-jasny" : "opacity-60",
                  ].join(" ")}
                >
                  <div className="min-w-0">
                    <div className="text-xs text-tekst-slaby">{nicki[wpis.playerId] ?? "?"}</div>
                    <div className="truncate">
                      {wpis.display || <span className="text-tekst-slaby">—</span>}
                    </div>
                  </div>
                  <div className="shrink-0 flex gap-2 items-center">
                    {opisStatusu[wpis.status] ? (
                      <Odznaka zielona={wpis.status === "dictionary"}>
                        {opisStatusu[wpis.status]}
                      </Odznaka>
                    ) : null}
                    {kwestionujacy.length > 0 ? (
                      <Odznaka aktywna>{kwestionujacy.length} ✕</Odznaka>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        </Karta>
      ))}

      <div className="sticky bottom-0 pt-2 bezpieczny-dol bg-tlo">
        <Przycisk
          wariant={jestemGotowy ? "gorny" : "glowny"}
          pelna
          onClick={onGotowe}
          disabled={jestemGotowy}
          className="min-h-16"
        >
          {jestemGotowy ? `Czekam (${pm.ready.length}/${pm.progress.length})` : "Gotowe"}
        </Przycisk>
      </div>
    </>
  );
}
